/**
 * Mood Engine — the emotional temperature of the design.
 *
 * Each mood is a small vector of creative attributes (0–1) the downstream
 * engines read: energy drives movement and rhythm, sophistication drives
 * whitespace and restraint, warmth and playfulness drive palette and shape
 * choices. `paletteThemes` names the Template Factory palette themes that
 * express the mood, in preference order.
 */

export const MOODS = {
  premium: {
    id: 'premium', name: 'Premium',
    energy: 0.3, warmth: 0.4, sophistication: 0.9, playfulness: 0.1,
    contrast: 'high', spacingFeel: 'airy',
    paletteThemes: ['luxury', 'midnight', 'corporate'],
  },
  energetic: {
    id: 'energetic', name: 'Energetic',
    energy: 0.95, warmth: 0.6, sophistication: 0.35, playfulness: 0.7,
    contrast: 'high', spacingFeel: 'compact',
    paletteThemes: ['vibrant', 'warm', 'midnight'],
  },
  fresh: {
    id: 'fresh', name: 'Fresh',
    energy: 0.6, warmth: 0.35, sophistication: 0.5, playfulness: 0.45,
    contrast: 'medium', spacingFeel: 'comfortable',
    paletteThemes: ['fresh', 'pastel', 'vibrant'],
  },
  healthy: {
    id: 'healthy', name: 'Healthy',
    energy: 0.5, warmth: 0.45, sophistication: 0.5, playfulness: 0.3,
    contrast: 'medium', spacingFeel: 'airy',
    paletteThemes: ['fresh', 'earthy', 'pastel'],
  },
  warm: {
    id: 'warm', name: 'Warm',
    energy: 0.5, warmth: 0.95, sophistication: 0.45, playfulness: 0.4,
    contrast: 'medium', spacingFeel: 'comfortable',
    paletteThemes: ['warm', 'earthy', 'vibrant'],
  },
  elegant: {
    id: 'elegant', name: 'Elegant',
    energy: 0.25, warmth: 0.5, sophistication: 0.85, playfulness: 0.1,
    contrast: 'medium', spacingFeel: 'airy',
    paletteThemes: ['pastel', 'luxury', 'corporate'],
  },
  luxury: {
    id: 'luxury', name: 'Luxury',
    energy: 0.2, warmth: 0.35, sophistication: 1, playfulness: 0.05,
    contrast: 'high', spacingFeel: 'airy',
    paletteThemes: ['luxury', 'midnight'],
  },
  playful: {
    id: 'playful', name: 'Playful',
    energy: 0.8, warmth: 0.7, sophistication: 0.2, playfulness: 1,
    contrast: 'medium', spacingFeel: 'comfortable',
    paletteThemes: ['pastel', 'vibrant', 'fresh'],
  },
  bold: {
    id: 'bold', name: 'Bold',
    energy: 0.85, warmth: 0.5, sophistication: 0.5, playfulness: 0.4,
    contrast: 'high', spacingFeel: 'compact',
    paletteThemes: ['vibrant', 'midnight', 'warm'],
  },
  minimal: {
    id: 'minimal', name: 'Minimal',
    energy: 0.2, warmth: 0.3, sophistication: 0.8, playfulness: 0.1,
    contrast: 'medium', spacingFeel: 'airy',
    paletteThemes: ['corporate', 'pastel', 'luxury'],
  },
};

export const listMoods = () =>
  Object.values(MOODS).map(({ id, name }) => ({ id, name }));

export const getMood = (id) => {
  const mood = MOODS[id];
  if (!mood) throw new Error(`Unknown mood "${id}"`);
  return mood;
};
